import React, { useState, useRef, useEffect } from 'react';

import { ReactComponent as CameraIcon } from '@repo/common/assets/icons/16px/icon_16px_camera.svg';

import {
  BaseButton,
  Modal,
  ImageCropModal,
  useAuth,
  ImageUploadList,
  Toast,
  useT,
} from '@repo/common';
import {
  deleteProfileImage,
  uploadProfileImage,
} from '../../../../api/user/user.api';

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const MIN_DIMENSION = 200;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const PersonalInfoProfileImage = ({ onImageChange }) => {
  const profileImageUrl = `${process.env.REACT_APP_ASSETS_URL}/img-profile.svg`;
  const { user, refreshUser } = useAuth();
  const t = useT();

  // AdditionalInfoForm 과 같은 검증·문구
  const IMAGE_ERROR_MESSAGES = {
    FILE_TOO_LARGE: t(
      'File too large. Please upload an image under 2MB.',
      '파일이 너무 큽니다. 2MB 이하 이미지를 올려주세요.',
    ),
    TOO_SMALL: t(
      'Image too small. Please upload at least 200x200px.',
      '이미지가 너무 작습니다. 200x200px 이상으로 올려주세요.',
    ),
    INVALID_TYPE: t('Please upload JPG or PNG.', 'JPG 또는 PNG 파일을 올려주세요.'),
    OTHER: t('Upload failed. Please try again.', '업로드에 실패했습니다. 다시 시도해 주세요.'),
    REMOVE_FAILED: t(
      "Couldn't remove photo. Please try again.",
      '사진을 삭제하지 못했습니다. 다시 시도해 주세요.',
    ),
  };

  const profileRef = useRef(null);
  const fileInputRef = useRef(null);
  const wrapperRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [profileImage, setProfileImage] = useState(user?.profileUrl || null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [showUploadList, setShowUploadList] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const [toast, setToast] = useState(null); // { type: 'error' | 'success', message }

  useEffect(() => {
    // 서버 값이 갱신되면(다른 곳에서 바뀐 경우 포함) 따라간다
    setProfileImage(user?.profileUrl || null);
  }, [user?.profileUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowUploadList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
    // 같은 메시지를 연속으로 띄워도 다시 나타나게 한 틱 뒤에 세팅
    setTimeout(() => setToast({ type, message }), 10);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  const showErrorToast = (key = 'OTHER') =>
    showToast('error', IMAGE_ERROR_MESSAGES[key] || IMAGE_ERROR_MESSAGES.OTHER);

  const errorKeyFromResponse = (error) => {
    const status = error?.response?.status;
    const msg = error?.response?.data?.msg || '';
    if (status === 413 || msg.includes('2MB')) return 'FILE_TOO_LARGE';
    if (status === 400) return 'INVALID_TYPE';
    return 'OTHER';
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();
    if (isSaving) return;
    if (!showUploadList) {
      setShowUploadList(true);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleRemovePhoto = async () => {
    setShowDeleteConfirmModal(false);
    setIsSaving(true);
    try {
      await deleteProfileImage();
      setProfileImage(null);
      onImageChange?.(null);
      await refreshUser();
      showToast(
        'success',
        t('Profile photo removed.', '프로필 사진이 삭제되었습니다.'),
      );
    } catch (error) {
      console.error('Profile image delete failed:', error);
      showErrorToast('REMOVE_FAILED');
    } finally {
      setIsSaving(false);
    }
  };

  const validateImageFile = async (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      showErrorToast('INVALID_TYPE');
      return false;
    }
    if (file.size > MAX_FILE_BYTES) {
      showErrorToast('FILE_TOO_LARGE');
      return false;
    }

    const image = new Image();
    const url = URL.createObjectURL(file);
    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = url;
      });
    } catch {
      showErrorToast('INVALID_TYPE');
      return false;
    } finally {
      URL.revokeObjectURL(url);
    }

    if (image.width < MIN_DIMENSION || image.height < MIN_DIMENSION) {
      showErrorToast('TOO_SMALL');
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) return;

    const isValid = await validateImageFile(file);
    if (!isValid) return;

    setSelectedImage(URL.createObjectURL(file));
    setShowCropModal(true);
    setShowUploadList(false);
  };

  const handleCropConfirm = async (cropped) => {
    setShowCropModal(false);
    setIsSaving(true);
    try {
      // 서버 저장이 끝난 뒤에만 화면에 반영 — 실패했는데 바뀐 것처럼 보이지 않게
      await uploadProfileImage(cropped);
      setProfileImage(cropped);
      onImageChange?.(cropped);
      await refreshUser();
      showToast(
        'success',
        t('Profile photo updated.', '프로필 사진이 변경되었습니다.'),
      );
    } catch (error) {
      console.error('Profile image upload failed:', error);
      showErrorToast(errorKeyFromResponse(error));
    } finally {
      setIsSaving(false);
      if (selectedImage) URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
  };

  return (
    <div className='flex flex-col items-start gap-6 tabletH:max-w-[568px] w-full'>
      <div
        ref={profileRef}
        onClick={handleProfileClick}
        className={`relative flex items-center justify-center w-[90px] tabletH:w-[124px] tabletH:h-[124px] group tabletH:mb-[14px] ${
          isSaving ? 'cursor-wait opacity-60' : 'cursor-pointer'
        }`}
      >
        {profileImage ? (
          <img
            src={profileImage}
            alt='profile'
            className='w-full h-full rounded-full object-cover'
          />
        ) : (
          <img
            src={profileImageUrl}
            alt='default profile'
            loading='lazy'
            decoding='async'
            className='w-full h-full rounded-full'
          />
        )}

        <BaseButton
          type='text'
          size='s'
          icon={<CameraIcon />}
          bgclass='bg-grayscale-gray00 absolute bottom-0 right-1 rounded-full'
          // 호버·눌림 시 색·배경·축소 효과 없이 커서만 바뀐다
          className='hover:bg-grayscale-gray00 hover:text-grayscale-gray07 active:bg-grayscale-gray00 active:text-grayscale-gray07 active:scale-100'
        />

        {showUploadList && (
          <div ref={wrapperRef} className='absolute top-full left-[60%] z-10'>
            <ImageUploadList
              image={profileImage}
              onUploadClick={handleUploadClick}
              onRemoveClick={() => setShowDeleteConfirmModal(true)}
            />
          </div>
        )}
      </div>

      <input
        type='file'
        accept='.jpg,.jpeg,.png'
        ref={fileInputRef}
        className='hidden'
        onChange={handleFileChange}
      />

      {showCropModal && (
        <ImageCropModal
          image={selectedImage}
          onCancel={() => {
            setShowCropModal(false);
            if (selectedImage) URL.revokeObjectURL(selectedImage);
            setSelectedImage(null);
          }}
          onConfirm={handleCropConfirm}
          aspect={1}
          initialCropSize={{ width: 240, height: 240 }}
        />
      )}

      {toast && (
        <div className='fixed top-[60px] left-1/2 -translate-x-1/2 z-[1000]'>
          <Toast
            type={toast.type}
            state='solid'
            title={toast.message}
            hierarchy='small'
            direction='down'
            customWidth='auto'
          />
        </div>
      )}

      {showDeleteConfirmModal && (
        <Modal
          isOpen
          onClickLeftBtn={() => setShowDeleteConfirmModal(false)}
          onClickRightBtn={handleRemovePhoto}
          title={t('Remove profile photo?', '프로필 사진을 삭제할까요?')}
          subText={t('Your profile photo will be removed.', '프로필 사진이 삭제됩니다.')}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          rightButtonLabel={t('remove', '삭제')}
          type='warning'
        />
      )}
    </div>
  );
};

export default PersonalInfoProfileImage;
